import { UserMe } from "seenspire-api-client";

/**
 * Minimal structural shape of the account fields we send to PostHog as person
 * properties. Kept structural (not the generated `Account8`) so this module
 * stays decoupled from client-type quirks; the account objects on `UserMe`
 * assign to it directly.
 */
export interface IdentifyAccount {
  id?: number;
  status?: string;
  organization_info?: { name?: string };
  subscriptions?: Array<{
    plan?: { slug?: string };
    created_at?: Date | string;
  }>;
}

interface PostHog {
  identify: (distinctId: string, properties?: Record<string, unknown>) => void;
  setPersonProperties: (properties: Record<string, unknown>) => void;
  reset: () => void;
}

declare global {
  interface Window {
    posthog?: PostHog;
    __posthogEnabled?: boolean;
  }
}

/**
 * Opaque, per-user distinct_id derived entirely on the frontend: the SHA-256 of
 * the user's normalized (trimmed, lowercased) email. PostHog only ever sees this
 * hash — never the raw email, name, or company. The lead-holding partner
 * reproduces the same hash from the email they already hold to find the user's
 * recording, so no backend id or lookup is needed.
 *
 * Returns undefined when the email is missing or Web Crypto is unavailable — in
 * which case we simply do not identify (we never fall back to the raw email).
 */
export const hashEmail = async (
  email: string | undefined
): Promise<string | undefined> => {
  const normalized = (email || "").trim().toLowerCase();
  if (!normalized) return undefined;

  const subtle =
    typeof crypto !== "undefined" && crypto.subtle ? crypto.subtle : undefined;
  if (!subtle) return undefined;

  const bytes = new TextEncoder().encode(normalized);
  const digest = await subtle.digest("SHA-256", bytes);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
};

// PostHog is usable only when the loader snippet actually called init (non-empty
// key). Before init, `__posthogEnabled` is falsy and we no-op. The loader stub
// safely queues calls made after init but before the async script finishes.
const getPostHog = (): PostHog | undefined => {
  if (typeof window === "undefined") return undefined;
  if (!window.__posthogEnabled) return undefined;
  return window.posthog;
};

/**
 * Identify the user in PostHog by the opaque SHA-256 of their email ONLY (never
 * the raw email/name/company). No-ops unless PostHog is enabled AND an email is
 * present. Fire-and-forget: hashing is async, but call sites need not await.
 */
export const identifyUser = (user: UserMe, account?: IdentifyAccount): void => {
  if (!getPostHog()) return;

  void hashEmail(user ? user.email : undefined).then((distinctId) => {
    const posthog = getPostHog();
    if (!posthog || !distinctId) return;

    const subscription =
      account && account.subscriptions ? account.subscriptions[0] : undefined;

    posthog.identify(distinctId);
    posthog.setPersonProperties({
      account_id: account ? account.id : undefined,
      account_name:
        account && account.organization_info
          ? account.organization_info.name
          : undefined,
      plan:
        subscription && subscription.plan ? subscription.plan.slug : undefined,
      plan_status: account ? account.status : undefined,
      signup_date: subscription ? subscription.created_at : undefined,
    });
  });
};

/**
 * Clear the identified user on logout so two people on a shared browser don't
 * merge into one PostHog person. No-ops unless PostHog is enabled.
 */
export const resetUser = (): void => {
  const posthog = getPostHog();
  if (!posthog) return;
  posthog.reset();
};
